import React from "react";
import { MenuItem, Select } from "@mui/material";

const EMPTY_TERMS = [];

const termLabel = (term) =>
  term ? `${term.name}${term.is_semester_term ? " (semester)" : ""}` : "";

// Compact chip styling for a select that sits beside a page title: no fixed
// width (it sizes to the selected value), tighter padding/font than
// size="small", and the arrow pulled to the edge with the right padding set to
// just clear it. Exported for the handful of non-term header selects (e.g. the
// Students grade-level menu) that have to match this one.
export const headerSelectSx = {
  flexShrink: 0,
  fontSize: 13,
  "& .MuiSelect-select": { py: 0.5, pl: 1, pr: "22px" },
  "& .MuiSelect-icon": { right: 0 },
};

// The term dropdown that sits beside a page title. PageHeader renders this from
// its `termSelector` prop - pages should not mount it themselves, so that every
// term picker lands in the same slot with the same styling and behaviour.
//
// No floating InputLabel: the header sits flush to the navbar, so a label
// animating up would clip behind it. An inline placeholder (displayEmpty +
// renderValue) keeps the control compact instead.
//
// `value` is matched against `terms[].id` as a string, so a page may hand over
// either the numeric id from its payload or the raw string it read out of the
// URL. What comes back through `onChange` is always the id in the form `terms`
// carries it (numeric, in every payload feeding this), or null when the empty
// option is picked.
//
// `allowEmpty` turns the empty label into a real, selectable option. Only pages
// with no default term want it - Social Development shows a prompt until a term
// is picked, and picking the empty option returns to that prompt. Everywhere
// else the label is just a placeholder for the no-selection state.
export default function HeaderTermSelect({
  terms = EMPTY_TERMS,
  value,
  onChange,
  allowEmpty = false,
  emptyLabel = "Select term",
  sx,
}) {
  // No control until there are terms to pick: the legacy selectors rendered
  // nothing for an empty list, so pages whose list can legitimately come back
  // empty (e.g. only not-yet-started terms) hide the dropdown instead of
  // offering an empty menu. This also covers the first paint while the page is
  // still fetching.
  if (terms.length === 0) return null;

  // Resolve to the id one of the rendered MenuItems actually carries: an
  // unmatched value trips MUI's out-of-range warning.
  const selected = terms.find((t) => String(t.id) === String(value));

  return (
    <Select
      size="small"
      value={selected ? selected.id : ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      displayEmpty
      inputProps={{ "aria-label": "Term" }}
      renderValue={(v) =>
        termLabel(terms.find((t) => t.id === v)) || emptyLabel
      }
      sx={{ ...headerSelectSx, ...sx }}
    >
      {allowEmpty && <MenuItem value="">{emptyLabel}</MenuItem>}
      {terms.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {termLabel(t)}
        </MenuItem>
      ))}
    </Select>
  );
}
